import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase.js'
import { Button } from '@/components/ui/button.jsx'
import { Field } from '@/components/ui/field.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'

/**
 * The landing page for a password-reset link (Phase 2, item 18a — D28b).
 *
 * ── WHY THIS PAGE PARSES THE URL ITSELF, RATHER THAN detectSessionInUrl ─────
 * lib/supabase.js sets `detectSessionInUrl: false` DELIBERATELY, with the
 * reasoning recorded there: "MBOS has no OAuth redirect flow; leaving this on
 * makes the client parse every URL fragment on load looking for tokens that
 * will never be there" — on every page, every load. Turning that global
 * setting on to serve this one screen would reverse a Phase 1b decision for
 * every OTHER screen too. So this page does the equivalent work itself,
 * scoped to exactly the one route that needs it.
 *
 * Handles both shapes Supabase can deliver, because the project's configured
 * auth flow (PKCE vs implicit) is a Supabase Dashboard setting this code
 * should not have to assume: a `code` query parameter (PKCE — the modern
 * default) exchanged via exchangeCodeForSession, or `access_token` /
 * `refresh_token` in the URL FRAGMENT (implicit) applied via setSession.
 */
export function ResetPasswordPage () {
  const navigate = useNavigate()
  const [status, setStatus] = React.useState('verifying') // verifying | ready | invalid
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [done, setDone] = React.useState(false)

  React.useEffect(() => {
    let active = true

    async function establishRecoverySession () {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')

      if (code) {
        const { error: exErr } = await supabase.auth.exchangeCodeForSession(code)
        if (!active) return
        if (exErr) { setStatus('invalid'); return }
        window.history.replaceState({}, '', window.location.pathname)
        setStatus('ready')
        return
      }

      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const accessToken = hash.get('access_token')
      const refreshToken = hash.get('refresh_token')

      if (accessToken && refreshToken && hash.get('type') === 'recovery') {
        const { error: setErr } = await supabase.auth.setSession({
          access_token: accessToken, refresh_token: refreshToken
        })
        if (!active) return
        if (setErr) { setStatus('invalid'); return }
        window.history.replaceState({}, '', window.location.pathname)
        setStatus('ready')
        return
      }

      // No recovery credential in the URL at all — someone landed here
      // directly rather than through a real reset link.
      if (active) setStatus('invalid')
    }

    establishRecoverySession()
    return () => { active = false }
  }, [])

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Use at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Those passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password })
      if (updErr) throw updErr
      setDone(true)
    } catch (err) {
      setError(err?.message ?? 'Could not set the new password. Try again.')
      setSubmitting(false)
    }
  }

  if (done) return <Navigate to="/" replace />

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">MBOS</h1>
          <p className="mt-1.5 text-sm text-ink-muted">Set a new password</p>
        </div>

        {status === 'verifying' && (
          <div className="flex justify-center py-8"><Spinner label="Checking your link" /></div>
        )}

        {status === 'invalid' && (
          <div className="space-y-4 rounded-lg border border-line bg-surface p-6 shadow-card">
            <Alert tone="danger" title="This link is not valid">
              It may have expired or already been used. Request a new one.
            </Alert>
            <Button className="w-full" onClick={() => navigate('/forgot-password')}>
              Request a new link
            </Button>
          </div>
        )}

        {status === 'ready' && (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-lg border border-line bg-surface p-6 shadow-card"
            noValidate
          >
            {error && <Alert tone="danger">{error}</Alert>}

            <Field
              id="password"
              label="New password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
            <Field
              id="confirm"
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />

            <Button type="submit" className="w-full" loading={submitting}>
              {submitting ? 'Saving' : 'Set new password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
